import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { I18nProvider } from './lib/i18n';
import AuthPage from './pages/AuthPage';
import AuthConfirm from './pages/AuthConfirm';
import SignPage from './pages/SignPage';
import Dashboard from './pages/Dashboard';
import NewSubmission from './pages/NewSubmission';
import History from './pages/History';
import SubmissionDetail from './pages/SubmissionDetail';
import Clients from './pages/Clients';
import Profile from './pages/Profile';
import { InstallPrompt } from './components/InstallPrompt';
import { PushAutoSubscribe } from './components/PushAutoSubscribe';
import Stats from './pages/Stats';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Support from './pages/Support';
import DeleteAccount from './pages/DeleteAccount';
import Pricing from './pages/Pricing';
import BillingSuccess from './pages/BillingSuccess';
import ResetPassword from './pages/ResetPassword';
import AdminWaitlist from './pages/AdminWaitlist';
import AdminDashboard from './pages/AdminDashboard';
import Suppliers from './pages/Suppliers';
import SupplierDetail from './pages/SupplierDetail';
import Templates from './pages/Templates';
import Team from './pages/Team';
import TeamJoin from './pages/TeamJoin';
import Onboarding, { hasSeenOnboarding } from './pages/Onboarding';
import Download from './pages/Download';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import InvoiceEdit from './pages/InvoiceEdit';
import { SubscriptionGate } from './components/SubscriptionGate';
import { ProvinceConfirmModal } from './components/ProvinceConfirmModal';
import '@/App.css';

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-gray-400 font-bold uppercase tracking-widest text-xs">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  // First-time visitors who have never seen the onboarding get redirected to
  // /welcome. Already-logged-in users skip the onboarding and go straight in.
  if (user) {
    // Honor ?next=/some/path so post-login redirects work (e.g. team invitation
    // links). Only relative paths are accepted to prevent open-redirect abuse.
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
    return <Navigate to={safeNext} replace />;
  }
  if (!hasSeenOnboarding()) return <Navigate to="/welcome" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />
      <Route path="/welcome" element={<Onboarding />} />
      <Route path="/auth/confirm" element={<AuthConfirm />} />
      <Route path="/sign/:token" element={<SignPage />} />
      <Route path="/" element={<Protected><SubscriptionGate><Dashboard /></SubscriptionGate></Protected>} />
      <Route path="/submissions" element={<Protected><SubscriptionGate><History /></SubscriptionGate></Protected>} />
      <Route path="/submissions/new" element={<Protected><SubscriptionGate><NewSubmission /></SubscriptionGate></Protected>} />
      <Route path="/submissions/:id" element={<Protected><SubscriptionGate><SubmissionDetail /></SubscriptionGate></Protected>} />
      <Route path="/submissions/:id/edit" element={<Protected><SubscriptionGate><NewSubmission /></SubscriptionGate></Protected>} />
      <Route path="/clients" element={<Protected><SubscriptionGate><Clients /></SubscriptionGate></Protected>} />
      <Route path="/invoices" element={<Protected><SubscriptionGate><Invoices /></SubscriptionGate></Protected>} />
      <Route path="/invoices/:id" element={<Protected><SubscriptionGate><InvoiceDetail /></SubscriptionGate></Protected>} />
      <Route path="/invoices/:id/edit" element={<Protected><SubscriptionGate><InvoiceEdit /></SubscriptionGate></Protected>} />
      <Route path="/suppliers" element={<Protected><Suppliers /></Protected>} />
      <Route path="/suppliers/:id" element={<Protected><SupplierDetail /></Protected>} />
      <Route path="/templates" element={<Protected><SubscriptionGate><Templates /></SubscriptionGate></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/team/join" element={<TeamJoin />} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/stats" element={<Protected><SubscriptionGate><Stats /></SubscriptionGate></Protected>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/support" element={<Support />} />
      <Route path="/contact" element={<Support />} />
      <Route path="/help" element={<Support />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/supprimer-compte" element={<DeleteAccount />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/download" element={<Download />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/admin/waitlist" element={<AdminWaitlist />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <DataProvider>
          <BrowserRouter>
            <AppRoutes />
            <PushAutoSubscribe />
            <ProvinceConfirmModal />
            <InstallPrompt />
            <Toaster position="top-center" richColors closeButton />
          </BrowserRouter>
        </DataProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
